/**
 * Core Enum Entity Types Registry
 *
 * Defines metadata about entity types that can be associated with core enum options.
 * This registry helps with displaying human-readable names and understanding
 * which fields contain the entity's display name.
 */

export interface EntityTypeConfig {
  /** The entity type key used in the database */
  key: string;
  /** Human-readable display name for the entity type */
  displayName: string;
  /** The field name that contains the entity's display name */
  nameField: string;
  /** Optional description field */
  descriptionField?: string;
}

/**
 * Registry of all entity types that can be associated with core enum options.
 * Add new entity types here as they become taggable with enums.
 */
export const CORE_ENUM_ENTITY_TYPES: Record<string, EntityTypeConfig> = {
  rebate_product_group: {
    key: "rebate_product_group",
    displayName: "Rebate Product Group",
    nameField: "name",
    descriptionField: "description",
  },
  email_rule: {
    key: "email_rule",
    displayName: "Email Rule",
    nameField: "ruleName",
  },
  supplier: {
    key: "supplier",
    displayName: "Supplier",
    nameField: "name",
  },
  customer: {
    key: "customer",
    displayName: "Customer",
    nameField: "name",
  },
  product: {
    key: "product",
    displayName: "Product",
    nameField: "name",
    descriptionField: "description",
  },
  contact: {
    key: "contact",
    displayName: "Contact",
    nameField: "name",
  },
} as const;

/**
 * Get the configuration for an entity type
 */
export function getEntityTypeConfig(
  entityType: string,
): EntityTypeConfig | undefined {
  return CORE_ENUM_ENTITY_TYPES[entityType];
}

/**
 * Get the display name for an entity type
 */
export function getEntityTypeDisplayName(entityType: string): string {
  const config = CORE_ENUM_ENTITY_TYPES[entityType];
  if (config) {
    return config.displayName;
  }
  // Fallback: convert snake_case to Title Case
  return entityType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * List of all known entity type keys
 */
export const KNOWN_ENTITY_TYPES = Object.keys(
  CORE_ENUM_ENTITY_TYPES,
) as (keyof typeof CORE_ENUM_ENTITY_TYPES)[];
